import { useCheckoutContext } from "@/components/Checkout/CheckoutMachine/CheckoutMachineProvider";

import {
  AnalyticsTrackingEvent,
  Button,
  MOBILE_SCREEN_SIZE,
  StoreBrandingType,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { alpha, Box, styled } from "@mui/material";
import { useSelector } from "@xstate/react";
import ProgressTracker from "../components/ProgressAnimation";
// import { getCheckoutHeaderRenderConfig } from "@/components/Checkout/utils";
import { ArrowRightIcon } from "@brandclub/common-ui";

// import { ErrorDot } from "@/components/StoreComponents/StoreError";

import {
  CheckoutState,
  UserRetailerCheckoutPaymentVerificationCommunicationResponse,
} from "@/components/Checkout/CheckoutMachine/types/CheckoutMachine";
import StateBadge from "../components/StateBadge";
import {
  UserInteractionType,
  UserRetailerCheckoutOrderStatusV2,
} from "@/components/Checkout/types";
import { Link, useNavigate } from "react-router-dom";
import { useDashboardLinkPrefix } from "@/utils/hooks/useDashboardLinkPrefix";
import { formatMoney } from "@/utils/StringUtils";
import { useEffect, useState } from "react";
import { getRewardSummaryByScan } from "@/api/graphQl/authenticated/ServerSideSync";
import apolloClient from "@/redux/reduxApolloClient";
import { ScanResults } from "@/components/pages/AppPages/Sync/SyncSummary/SyncSummary";
// import { useGuestCheckoutEnabled } from "../../Checkout/CheckoutForm/components/useGuestCheckout";
import PaymentVerificationPopup from "../../Checkout/CheckoutForm/components/retailerConnect/PaymentVerificationPopup";
import EditConnectPopup, {
  PopupStatus,
} from "../../Checkout/CheckoutForm/components/retailerConnect/EditConnectPopup";
import OTPDialog from "../../Checkout/CheckoutForm/components/retailerConnect/EditConnectPopup/OTPDialog";
import { useAppSelector } from "@/redux/hooks";
import useStoreType from "@/utils/hooks/useStoreType";
import { StoreErrorV2 } from "@/components/StoreComponents/StoreError";
import StoreTimeCountingString from "@/components/StoreComponents/StoreTimeCountingString";
import { useGuestCheckoutEnabled } from "../../Checkout/CheckoutForm/components/useGuestCheckout";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",

  ".header_label_receipt": {
    display: "flex",
    alignItems: "center",
    gap: 24,
    color: theme.vars.palette.text.primary,
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "10px",
    flexDirection: "row",
    [theme.breakpoints.down("sm")]: {
      fontSize: "22px",
      marginBottom: "6px",
      alignItems: "flex-start",
      flexDirection: "column-reverse",
      gap: 8,
    },
  },

  ".header_label": {
    display: "flex",
    alignItems: "center",
    gap: 24,
    color: theme.vars.palette.text.primary,
    fontSize: "32px",
    fontWeight: "bold",
    marginBottom: "10px",
    flexDirection: "row",
    [theme.breakpoints.down("sm")]: {
      fontSize: "22px",
      marginBottom: "6px",
      alignItems: "flex-start",
      flexDirection: "column-reverse",
      gap: 8,
    },
  },
  ".header_sub_label": {
    color: theme.vars.palette.text.primary,
    fontSize: "18px",
    fontWeight: 500,
    marginBottom: "20px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: "16px",
      marginBottom: "16px",
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {},
  },
}));

const OrderNotificationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  gap: "24px",
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    gap: "16px",
  },
  ".order_notification": {
    flexDirection: "column",
    alignItems: "flex-start",
    marginTop: "25px",
    display: "flex",
    gap: "16px",
    color: theme.vars.palette.text.primary,

    fontSize: "20px",
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      marginTop: "16px",
      fontSize: "14px",
      gap: "8px",
    },
    ".auto-switch-to-guest-message": {
      fontSize: "20px",
      fontWeight: 500,
      color: theme.vars.palette.text.primary,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        fontSize: "14px",
      },
    },
  },
  ".buttons": {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      width: "100%",
    },
  },
}));

const OrderNotification = () => {
  const { actorRef, send } = useCheckoutContext();
  const order = useSelector(
    actorRef,
    (snapshot) => snapshot.context?.retailerCheckoutOrderStatus
  );

  const storeType = useStoreType();

  const isDtc = storeType === StoreBrandingType.CustomDTCStore;
  const domainConfig = useAppSelector((state) => state.appConfig?.domainConfig);

  const navigate = useNavigate();
  const { experimentEnabled } = useGuestCheckoutEnabled(
    order?.userRetailerCheckoutOrder?.retailerId
  );

  const retailers = useAppSelector((state) => state.retailers);

  const userInteractionRequirement =
    order?.userRetailerCheckoutOrder?.userInteractionRequirement;

  const handleSubmitPaymentVerification = (code: string) => {
    const interactionData: UserRetailerCheckoutPaymentVerificationCommunicationResponse =
      {
        value: code,
      };
    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.RETAILER_PAYMENT_VERIFICATION,
        interactionData,
      },
    });
  };

  const handleRetailerPaymentVerification = () => {
    PaymentVerificationPopup.show({
      title: userInteractionRequirement?.title ?? "Verify your payment method",
      subtitle: userInteractionRequirement?.displayMessage ?? "123",
      placeholder: userInteractionRequirement?.placeholder ?? "Code",
      onSave: ({ value }) => {
        handleSubmitPaymentVerification(value);
      },
      retailer: retailers.find(
        (retailer) =>
          retailer.retailerId === order?.userRetailerCheckoutOrder?.retailerId
      ),
    });
  };

  const handleRetailerCredentials = () => {
    const currentRetailer = retailers.find(
      (retailer) =>
        retailer.retailerId === order?.userRetailerCheckoutOrder?.retailerId
    );

    const currentUsername =
      order?.userRetailerCheckoutOrder?.userCheckoutInfo?.retailerConnection
        ?.username;

    EditConnectPopup.show({
      connectReward: currentRetailer?.retailerConnectRewardAmount ?? 0,
      guestCheckoutClick: () => {
        send({
          type: "ON_SWITCH_TO_GUEST",
        });
      },
      onSaveCredentials: async (email: string, password: string) => {
        // if not user interaction timeout, then submit user interaction
        if (
          order?.userRetailerCheckoutOrder?.status !==
          UserRetailerCheckoutOrderStatusV2.user_interaction_timeout
        ) {
          send({
            type: "SUBMIT_USER_INTERACTION",
            data: {
              interactionType: UserInteractionType.RETAILER_CREDENTIALS,

              interactionData: {
                credential: {
                  username: email,
                  password: password,
                },
                retailerId: currentRetailer?.retailerId ?? 0,
              },
            },
          });
        } else {
          send({
            type: "ON_RETRY_CHECKOUT",
            data: {
              retailerId: currentRetailer?.retailerId ?? 0,
              credential: {
                username: email,
                password: password,
              },
            },
          });
        }
      },
      defaultStatus: PopupStatus.CONNECT,
      currentUsername: currentUsername ?? "",
      currentPassword: "",
      retailer: currentRetailer,
      customSubtitle: userInteractionRequirement?.displayMessage,
      errorBorder: true,
    });
  };

  const handleOtpVerification = () => {
    const currentRetailer = retailers.find(
      (retailer) =>
        retailer.retailerId === order?.userRetailerCheckoutOrder?.retailerId
    );

    OTPDialog.show({
      retailer: currentRetailer,
      userInteractionRequirement,
      onSave: (otp: string) => {
        send({
          type: "SUBMIT_USER_INTERACTION",
          data: {
            interactionType: UserInteractionType.OTP_VERIFICATION,
            interactionData: { otpCode: otp },
          },
        });
      },
      onResend: () => {
        send({
          type: "SUBMIT_USER_INTERACTION",
          data: {
            interactionType: UserInteractionType.OTP_VERIFICATION,
            interactionData: { resend: true },
          },
        });
      },
    });
  };

  // const handleAddressSelection = () => {};

  const handleInventoryConfirmation = () => {
    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.INVENTORY_CONFIRMATION,
        interactionData: {
          approve: true,
        },
      },
    });
  };

  // const handleShippingOptionSelection = () => {};

  const handleNoInventoryAvailable = () => {
    if (
      isDtc &&
      (domainConfig?.customShopPageUrls?.popularProductsPage ||
        domainConfig?.customShopDomainName)
    ) {
      window.open(
        `https://${
          domainConfig?.customShopPageUrls?.popularProductsPage ||
          domainConfig?.customShopDomainName
        }`
      );
    } else {
      navigate(`/trending`);
    }
  };

  const handleActionButtonClick = () => {
    const interactionType =
      order?.headerDisplayConfig?.actionButton?.interactionType;

    switch (interactionType) {
      case UserInteractionType.RETAILER_PAYMENT_VERIFICATION:
        handleRetailerPaymentVerification();
        break;
      case UserInteractionType.RETAILER_CREDENTIALS:
        handleRetailerCredentials();
        break;
      case UserInteractionType.OTP_VERIFICATION:
        handleOtpVerification();
        break;
      case UserInteractionType.ADDRESS_SELECTION:
        // no-action required in header
        break;
      case UserInteractionType.INVENTORY_CONFIRMATION:
        handleInventoryConfirmation();
        break;
      case UserInteractionType.SHIPPING_OPTION_SELECTION:
        // no-action required
        break;
      case UserInteractionType.NO_INVENTORY_AVAILABLE:
        handleNoInventoryAvailable();
        break;
      default:
        break;
    }
  };

  return (
    <OrderNotificationContainer>
      <div className="order_notification">
        <div
          className="text"
          style={{
            fontWeight:
              experimentEnabled &&
              order?.headerDisplayConfig?.autoSwitchToGuestConfig?.message
                ? 700
                : 500,
          }}
        >
          {order?.headerDisplayConfig?.headerDescription}
        </div>
        {experimentEnabled &&
        order?.headerDisplayConfig?.autoSwitchToGuestConfig?.message ? (
          <StoreTimeCountingString
            className="auto-switch-to-guest-message"
            text={order?.headerDisplayConfig?.autoSwitchToGuestConfig?.message}
            endTime={
              order?.headerDisplayConfig?.autoSwitchToGuestConfig
                ?.expirationTime
            }
          />
        ) : null}
      </div>
      {order?.headerDisplayConfig?.actionButton?.text ? (
        <div className="buttons">
          <Button
            className="checkout-solid-button"
            fullWidth
            disableMaxWidth
            sx={{
              paddingLeft: "50px",
              paddingRight: "50px",
              fontSize: "16px",
              maxWidth: "unset",
              [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
                paddingLeft: "20px",
                paddingRight: "20px",
                fontSize: "14px",
                maxWidth: "100%",
                width: "100%",
              },
            }}
            onClick={handleActionButtonClick}
          >
            {order?.headerDisplayConfig?.actionButton?.text}
          </Button>
          {/* {headerRenderConfig.switchToGuest && experimentEnabled && (
            <Button
              variant="outline"
              sx={{
                paddingLeft: "30px",
                paddingRight: "30px",
                fontSize: "16px",
                maxWidth: "unset",
                [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  fontSize: "14px",
                },
              }}
              onClick={handleSwitchToGuest}
            >
              Switch to Guest Checkout
            </Button>
          )} */}
        </div>
      ) : null}
    </OrderNotificationContainer>
  );
};

const RewardContainer = styled("div")(({ theme }) => ({
  padding: "25px 0",
  display: "flex",
  color: theme.vars.palette.text.primary,
  width: "100%",
  boxSizing: "border-box",
  flexDirection: "row",
  justifyContent: "space-between",
  backgroundColor: alpha(theme.palette.secondary.main, 0.2),
  borderRadius: 8,
  alignItems: "center",
  paddingLeft: 25,
  paddingRight: 25,
  paddingVertical: 18,
  gap: 26,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    padding: "18px 0",
    paddingLeft: 25,
    paddingRight: 25,
    gap: 16,
  },
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: "18px 0",
    paddingLeft: 15,
    paddingRight: 15,
    gap: 16,
  },
  ".content": {
    gap: 8,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      gap: 4,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      gap: 4,
    },
  },
  ".title": {
    fontSize: 20,
    fontWeight: 600,
    color: theme.vars.palette.primary,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
  },
  ".text": {
    fontSize: 16,
    fontWeight: 500,
    color: theme.vars.palette.primary,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: 12,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 12,
    },
  },
  ".icon": {
    width: 70,
    height: 70,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "white",
    borderRadius: 50,

    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      width: 50,
      height: 50,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      width: 50,
      height: 50,
    },
    ".svg": {
      width: 42,
      height: 42,
      fill: theme.vars.palette.text.primary,
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        width: 32,
        height: 32,
      },
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        width: 32,
        height: 32,
      },
    },
  },
  ".button_icon": {
    width: 26,
    height: 26,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      width: 18,
      height: 18,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      width: 18,
      height: 18,
    },
  },
}));

const SuccessReward = ({ title, text }: { title: string; text: string }) => {
  const prefix = useDashboardLinkPrefix();
  return (
    <Link to={prefix + "/rewards/balance"} style={{ textDecoration: "none" }}>
      <RewardContainer>
        <div className="icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32.385 32.385"
            className="svg"
          >
            <path
              data-name="Path 25766"
              d="M15.179 1.9a4.258 4.258 0 0 1 4.528 0l3.025 1.9a1.966 1.966 0 0 0 .618.256l3.483.8a4.26 4.26 0 0 1 3.2 3.2l.8 3.483a1.966 1.966 0 0 0 .256.618l1.9 3.025a4.257 4.257 0 0 1 0 4.528l-1.9 3.025a1.966 1.966 0 0 0-.256.618l-.8 3.483a4.26 4.26 0 0 1-3.2 3.2l-3.483.8a1.966 1.966 0 0 0-.618.256l-3.025 1.9a4.257 4.257 0 0 1-4.528 0l-3.025-1.9a1.966 1.966 0 0 0-.618-.256l-3.483-.8a4.26 4.26 0 0 1-3.2-3.2l-.8-3.483a1.966 1.966 0 0 0-.256-.618L1.9 19.707a4.258 4.258 0 0 1 0-4.528l1.9-3.025a1.966 1.966 0 0 0 .256-.618l.8-3.483a4.26 4.26 0 0 1 3.2-3.2l3.483-.8a1.966 1.966 0 0 0 .618-.256l3.025-1.9zm2.264 4.62a10.921 10.921 0 1 0 10.921 10.923A10.927 10.927 0 0 0 17.443 6.522zm0 2.259a8.661 8.661 0 1 1-8.661 8.661 8.664 8.664 0 0 1 8.661-8.659zm0 12.069 2.071 1.274a1.37 1.37 0 0 0 2.062-1.436l-.5-2.481 1.609-1.61a1.37 1.37 0 0 0-.871-2.336l-2.162-.155-.952-2.223a1.37 1.37 0 0 0-2.519 0l-.955 2.225-2.162.155A1.37 1.37 0 0 0 12.2 16.6l1.609 1.61-.5 2.481a1.37 1.37 0 0 0 2.062 1.436z"
              fillRule="evenodd"
              transform="translate(-1.25 -1.25)"
            />
          </svg>
        </div>
        <div className="content" style={{ flex: 1 }}>
          <div className="title">{title}</div>
          <div className="text">{text}</div>
        </div>
        <ArrowRightIcon
          fontSize="large"
          className="button_icon"
          color="primary"
        />
      </RewardContainer>
    </Link>
  );
};

const ProcessingHead = () => {
  const { actorRef } = useCheckoutContext();

  const order = useSelector(
    actorRef,
    (snapshot) => snapshot.context?.retailerCheckoutOrderStatus
  );

  // const headerRenderConfig = getCheckoutHeaderRenderConfig({
  //   displayStage: order?.displayStatusInfo?.stage,
  //   order: order?.userRetailerCheckoutOrder,
  //   displayErrorMessage: order?.displayStatusInfo?.displayErrorMessage,
  // });

  const activeStage = useSelector(
    actorRef,
    (snapshot) => snapshot.context.activeStage
  );
  const errorStage = useSelector(
    actorRef,
    (snapshot) => snapshot.context.error?.stage
  );

  if (!order) {
    return null;
  }

  const showNeedsAttention =
    order?.userRetailerCheckoutOrder?.userInteractionRequirement?.type;

  return (
    <Container className="section">
      <div className="header_label">
        <div className="header_label_text">
          {order.headerDisplayConfig?.headerLabel}
        </div>
        {showNeedsAttention ? (
          <div className="header_label_pill ">
            <StoreErrorV2
              errorMessage={"Needs attention"}
              sx={{ margin: "0 0" }}
            ></StoreErrorV2>
          </div>
        ) : null}
      </div>
      <div className="header_sub_label">
        Order #{order?.userRetailerCheckoutOrder?.orderId}
      </div>
      <ProgressTracker activeStep={activeStage} errorStep={errorStage} />
      <OrderNotification />
    </Container>
  );
};

const ReceiptHead = () => {
  const { actorRef } = useCheckoutContext();
  const [scanResults, setScanResults] = useState<ScanResults>();
  const response = useSelector(
    actorRef,
    (snapshot) => snapshot.context?.retailerCheckoutOrderStatus
  );

  const displayStatusInfo = response?.displayStatusInfo;
  const order = response?.userRetailerCheckoutOrder;

  useEffect(() => {
    (async () => {
      try {
        if (
          response?.userRetailerCheckoutOrder?.syncId &&
          response?.userRetailerCheckoutOrder?.syncBatchId
        ) {
          const syncIds = [response?.userRetailerCheckoutOrder?.syncId];

          const { data } = await apolloClient.query({
            query: getRewardSummaryByScan,
            variables: {
              syncIds,
            },
          });

          setScanResults({
            ...data.RewardSummaryByScan,
          });
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [
    response?.userRetailerCheckoutOrder?.syncId,
    response?.userRetailerCheckoutOrder?.syncBatchId,
  ]);

  const totalRewardAmount =
    (scanResults?.rewardAmountUnlocked || 0) +
    (scanResults?.rewardAmountLocked || 0) +
    (scanResults?.rewardAmountPending || 0);

  return (
    <Container className="section">
      <div className="header_label_receipt">
        <div className="header_label_text">
          {response?.headerDisplayConfig?.headerLabel}
        </div>
        <div className="header_label_pill">
          <StateBadge type={displayStatusInfo?.shipmentStatus} />
        </div>
      </div>
      <div className="header_sub_label">
        Order #{response?.userRetailerCheckoutOrder?.orderId}
      </div>{" "}
      {order?.status === UserRetailerCheckoutOrderStatusV2.checkout_error ? (
        <>
          <OrderNotification />
        </>
      ) : null}
      {totalRewardAmount > 0 ? (
        <div style={{ width: "100%", marginTop: 20 }}>
          <SuccessReward
            title="Congratulations!"
            text={`You earned ${formatMoney(totalRewardAmount)} in new rewards`}
          />
        </div>
      ) : null}
    </Container>
  );
};

const CheckoutHead = () => {
  const { actorRef } = useCheckoutContext();
  const checkoutState = useSelector(
    actorRef,
    (snapshot) => snapshot.context.checkoutState
  );

  if (checkoutState === CheckoutState.RECEIPT_GENERATED) {
    return <ReceiptHead />;
  } else {
    return <ProcessingHead />;
  }
};

export default CheckoutHead;
